/* eslint-disable react-hooks/exhaustive-deps */
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { ReactSelectOption } from "../../Interfaces";
import { LiveboardCard } from "./LiveboardCard";
import { MetadataPermission } from "../../../services/types/thoughtSpot";
import {
  useLazyListGroupsQuery,
  useLazyListLiveboardsQuery,
  useLazyListUsersQuery,
} from "../../../services/thoughtSpotV1";
import {
  useLazyGetCognitoUsersQuery,
  useLazyGetTSAccessTokenQuery,
} from "../../../services/serverless";
import {
  useGetLiveboardsAccessableByGroupMutation,
  useGetUserDetailsMutation,
  useLazyGetLiveboardArrayPermissionsQuery,
} from "../../../services/thoughtSpotV2";
import { saveCSV } from "../../ExportToCSV";
import { getNameFromEmail } from "../../../helpers/miscHelpers";
import { GetCognitoUsersResponse } from "../../../services/types/gallus";

interface UserCheckToolProps {
  orgId: number;
  cognitoGroup: string;
  mode: "User" | "Group";
  userShouldHaveNames: boolean;
  userFilter?: string;
}

interface IDAndName {
  id: string;
  name: string;
}

interface UserCheckToolState {
  tsToken: string | null;
  selectedPrincipal: ReactSelectOption;
  principalList: ReactSelectOption[];
  loadingPrincipals: boolean;
  loadingSharingData: boolean;
  cards: JSX.Element[];
  error: {
    errorFlag: boolean;
    errorMessage: string;
  };
  liveboardAccess: {
    group: {
      userGroup: IDAndName;
      liveboardArray: MetadataPermission[];
    }[];
    directReadOnly: IDAndName[];
    directModify: IDAndName[];
  } | null;
  lifecycle: string | null;
  currentlyFetchingCSVData: boolean;
}

type Principal = {
  id: string;
  name: string;
  displayName: string;
  // other fields omitted for brevity
};

type PermissionEntry = {
  metadata_id: string;
  metadata_name: string;
  permission: "MODIFY" | "READ_ONLY";
};

type Liveboard = {
  metadata_name: string; // Liveboard name – X‑axis label
  metadata_id: string; // Liveboard id (not used here)
  permissions: PermissionEntry[];
};

/**
 * Helper – rank a permission so we can pick the highest one.
 * Higher number = stronger privilege.
 */
const permRank = (p: string): number => {
  switch (p) {
    case "MODIFY":
      return 2;
    case "READ_ONLY":
      return 1;
    default: // NO_ACCESS or unknown
      return 0;
  }
};

/**
 * Takes raw JSON strings (or already‑parsed objects) and returns CSV.
 *
 * @param principalsJson        JSON string or object representing the user list.
 * @param permissionsJson JSON string or object representing the liveboard permissions.
 * @returns CSV string where the first row is the header (Liveboard names) and each
 *          subsequent row starts with the user name followed by the permission
 *          for each liveboard.
 */
export function generateHeatmapCsv(
  principalsJson: string | Principal[],
  permissionsJson: string | Liveboard[],
  cognitoUserDetails?: GetCognitoUsersResponse,
): string {
  // Parse inputs (accept either raw JSON or already parsed objects)
  const principalsParsed: Principal[] =
    typeof principalsJson === "string"
      ? JSON.parse(principalsJson)
      : principalsJson;
  const liveboardsParsed: Liveboard[] =
    typeof permissionsJson === "string"
      ? JSON.parse(permissionsJson)
      : permissionsJson;

  // Build lookup maps for fast access
  const principalsNameArray = new Array<string>(); // all usernames for the Y‑axis
  for (const u of principalsParsed) {
    principalsNameArray.push(u.name);
  }

  // List of liveboard names – defines the X‑axis order
  const liveboardNamesArray = liveboardsParsed.map((lb) => lb.metadata_name);

  // Initialise matrix with "NO_ACCESS"
  const matrix: Record<string, Record<string, string>> = {}; // userName -> (liveboardName -> permission)
  for (const principalsName of principalsNameArray) {
    matrix[principalsName] = {};
    for (const lbName of liveboardNamesArray) {
      matrix[principalsName][lbName] = "NO_ACCESS";
    }
  }

  // Populate matrix with explicit permissions (taking the highest privilege)
  // We iterate over the liveboard array
  for (const lb of liveboardsParsed) {
    const lbName = lb.metadata_name;
    // We iterate over each individual permission of that liveboard
    for (const entry of lb.permissions) {
      const principalsName = entry.metadata_name; // matches User.name
      if (!principalsNameArray.includes(principalsName)) {
        // Skip entries for users not present in the master list
        // Useful in cases where the permission list has more data that we don't really need
        continue;
      }
      const current = matrix[principalsName][lbName];
      // Keep the higher‑ranked permission
      if (permRank(entry.permission) > permRank(current)) {
        matrix[principalsName][lbName] = entry.permission;
      }
    }
  }

  // Convert matrix to CSV
  const csvLines: string[] = [];

  // Header row: empty cell for the Y‑axis label + liveboard names
  // if cognitoUserDetails was set, add Full Name column
  if (cognitoUserDetails) {
    csvLines.push(["User", "Full Name", ...liveboardNamesArray].join(","));
  } else {
    csvLines.push(["User", ...liveboardNamesArray].join(","));
  }

  // One row per user (sorted alphabetically for readability)
  const sortedUserNames = Array.from(principalsNameArray).sort((a, b) =>
    a.localeCompare(b),
  );

  for (const userName of sortedUserNames) {
    let extraName: string | null = null;
    const row = [userName];
    if (cognitoUserDetails) {
      let cognitoDetails = cognitoUserDetails.userList.find(
        (userObject) => userObject.email === userName,
      );
      if (
        cognitoDetails &&
        cognitoDetails.family_name &&
        cognitoDetails.given_name
      ) {
        extraName =
          cognitoDetails.given_name + " " + cognitoDetails.family_name;
      } else {
        extraName = "Name not found";
      }

      row.push(extraName);
    }

    for (const lbName of liveboardNamesArray) {
      row.push(matrix[userName][lbName]);
    }
    csvLines.push(row.join(","));
  }

  return csvLines.join("\n");
}

/**
 * Tool to check the groups the user is in, and what liveboards he can interact with
 * according to those groups.
 * @param props
 * @returns
 */

export const UserCheckTool = (props: UserCheckToolProps) => {
  const [state, setState] = useState<UserCheckToolState>({
    tsToken: null,
    selectedPrincipal: { label: "", value: "" },
    principalList: [],
    loadingPrincipals: true,
    loadingSharingData: false,
    error: { errorFlag: false, errorMessage: "" },
    cards: [],
    liveboardAccess: null,
    lifecycle: null,
    currentlyFetchingCSVData: false,
  });

  const [tsAccessTokenQuery, tsAccessTokenResult] =
    useLazyGetTSAccessTokenQuery();
  const [listUsersQuery, listUsersResults] = useLazyListUsersQuery();
  const [listGroupsQuery, listGroupsResults] = useLazyListGroupsQuery();
  const [getUserDetails] = useGetUserDetailsMutation();
  const [liveboardsAccessableByGroupOrUser] =
    useGetLiveboardsAccessableByGroupMutation();
  const [listLiveboards] = useLazyListLiveboardsQuery();
  const [getPermissions] = useLazyGetLiveboardArrayPermissionsQuery();
  const [lazyGetCognitoUsersQuery, lazyGetCognitoUsersResults] =
    useLazyGetCognitoUsersQuery();

  useEffect(() => {
    tsAccessTokenQuery(props.orgId);
  }, []);

  useEffect(() => {
    if (
      tsAccessTokenResult.isSuccess &&
      tsAccessTokenResult.status === "fulfilled"
    ) {
      setState((prevState) => ({
        ...prevState,
        tsToken: tsAccessTokenResult.data.token,
        selectedPrincipal: { label: "", value: "" },
        principalList: [],
        cards: [],
        liveboardAccess: null,
        lifecycle: null,
        currentlyFetchingCSVData: false,
      }));
      try {
        if (props.mode === "User") {
          listUsersQuery({
            thoughtSpotAccessToken: tsAccessTokenResult.data.token,
            filter: props.userFilter ?? "",
          });
        } else {
          listGroupsQuery({
            thoughtSpotAccessToken: tsAccessTokenResult.data.token,
          });
        }
      } catch {
        tsAccessTokenQuery(props.orgId);
      }
    }
  }, [tsAccessTokenResult, props]);

  async function handleClickGetCSV(name: string) {
    setState((prevState) => ({ ...prevState, currentlyFetchingCSVData: true }));
    if (state.tsToken) {
      const listOfLiveboards = await listLiveboards({
        thoughtSpotAccessToken: state.tsToken,
      }).unwrap();

      const listOfPrincipals =
        props.mode === "User"
          ? await listUsersQuery({
              thoughtSpotAccessToken: state.tsToken,
              filter: "",
            }).unwrap()
          : await listGroupsQuery({
              thoughtSpotAccessToken: state.tsToken,
            }).unwrap();

      const permissions = await getPermissions({
        thoughtSpotAccessToken: state.tsToken,
        liveboardArray: listOfLiveboards.map((liveboard) => {
          return liveboard.id;
        }),
      }).unwrap();

      let csvFile: string = "";

      if (props.mode === "User" && props.userShouldHaveNames) {
        const cognitoUserDetails = await lazyGetCognitoUsersQuery({
          cognitoGroup: props.cognitoGroup,
          nextToken: null,
          miscParams: "disablePagination",
        }).unwrap();
        csvFile = generateHeatmapCsv(
          listOfPrincipals,
          permissions,
          cognitoUserDetails,
        );
      } else {
        csvFile = generateHeatmapCsv(listOfPrincipals, permissions);
      }

      saveCSV(name, csvFile);
      setState((prevState) => ({
        ...prevState,
        currentlyFetchingCSVData: false,
      }));
    }
  }

  useEffect(() => {
    const makeUserList = async () => {
      if (
        listUsersResults.isSuccess &&
        listUsersResults.status === "fulfilled"
      ) {
        setState((prevState) => ({ ...prevState, loadingPrincipals: true }));
        let formattedUserList: ReactSelectOption[] = [];
        if (props.userShouldHaveNames) {
          const cognitoUserDetails = await lazyGetCognitoUsersQuery({
            cognitoGroup: props.cognitoGroup,
            nextToken: null,
            miscParams: "disablePagination",
          }).unwrap();
          listUsersResults.data.forEach((elementUser) => {
            const currentUserDetails = cognitoUserDetails.userList.find(
              (userObject) =>
                userObject.username ===
                getNameFromEmail(elementUser.displayName),
            );

            let finalLabel = elementUser.name;

            if (
              currentUserDetails?.family_name &&
              currentUserDetails?.given_name
            ) {
              finalLabel =
                currentUserDetails?.family_name +
                ", " +
                currentUserDetails?.given_name;
            }

            formattedUserList.push({
              label: finalLabel,
              value: elementUser.name,
            });
          });
          formattedUserList.sort((a, b) => {
            const nameA = a.label.toUpperCase();
            const nameB = b.label.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }

            return 0;
          });
          setState((prevState) => ({
            ...prevState,
            principalList: formattedUserList,
            loadingPrincipals: false,
          }));
        } else {
          listUsersResults.data.forEach((elementUser) => {
            formattedUserList.push({
              label: elementUser.name,
              value: elementUser.id,
            });
          });
          setState((prevState) => ({
            ...prevState,
            principalList: formattedUserList,
            loadingPrincipals: false,
          }));
        }
      }
    };
    makeUserList();
  }, [listUsersResults]);

  useEffect(() => {
    if (
      listGroupsResults.isSuccess &&
      listGroupsResults.status === "fulfilled"
    ) {
      setState((prevState) => ({ ...prevState, loadingPrincipals: true }));
      let formattedGroupList: ReactSelectOption[] = [];
      listGroupsResults.data.forEach((elementUser) => {
        formattedGroupList.push({
          label: elementUser.name,
          value: elementUser.id,
        });
      });
      setState((prevState) => ({
        ...prevState,
        principalList: formattedGroupList,
        loadingPrincipals: false,
      }));
    }
  }, [listGroupsResults]);

  async function handleGetUserInfo() {
    if (state.tsToken && props.mode === "User") {
      setState((prevState) => ({ ...prevState, loadingSharingData: true }));
      const userDetails = await getUserDetails({
        userId: state.selectedPrincipal.value,
        thoughtSpotAccessToken: state.tsToken,
      }).unwrap();

      const userGroups: { id: string; name: string }[] =
        userDetails[0].user_groups;

      setState((prevState) => ({
        ...prevState,
        lifecycle: "Fetching liveboards shared directly with user",
      }));
      const liveboardResults = await liveboardsAccessableByGroupOrUser({
        thoughtSpotAccessToken: state.tsToken,
        IdArray: [{ identifier: state.selectedPrincipal.value, type: "USER" }],
      }).unwrap();

      var visibleLiveboardsReadOnly: { name: string; id: string }[] = [];
      var visibleLiveboardsModify: { name: string; id: string }[] = [];
      var systemLiveboards: { name: string; id: string }[] = [];

      if (
        liveboardResults &&
        liveboardResults[0].metadata_permission_info.length !== 0
      ) {
        const arrayOfPermissions =
          liveboardResults[0].metadata_permission_info.find(
            (arrayValue) => arrayValue.metadata_type === "LIVEBOARD",
          );
        if (arrayOfPermissions && arrayOfPermissions.metadata_permissions) {
          arrayOfPermissions.metadata_permissions.forEach((value) => {
            if (!value.metadata_name.includes("(Sample)")) {
              if (value.permission === "READ_ONLY") {
                visibleLiveboardsReadOnly.push({
                  name: value.metadata_name,
                  id: value.metadata_id,
                });
              } else {
                visibleLiveboardsModify.push({
                  name: value.metadata_name,
                  id: value.metadata_id,
                });
              }
            } else {
              systemLiveboards.push({
                name: value.metadata_name,
                id: value.metadata_id,
              });
            }
          });
        }
      }
      const groupsAndLiveboardAccess: {
        userGroup: { id: string; name: string };
        liveboardArray: MetadataPermission[];
      }[] = [];

      for (let i = 0; i < userGroups.length; i++) {
        setState((prevState) => ({
          ...prevState,
          lifecycle:
            "Fetching liveboards accessible by group " + userGroups[i].name,
        }));
        const liveboardsAccessableByGroups =
          await liveboardsAccessableByGroupOrUser({
            thoughtSpotAccessToken: state.tsToken,
            IdArray: [{ identifier: userGroups[i].id, type: "USER_GROUP" }],
          }).unwrap();
        const liveboardsAccessableByThisGroup: MetadataPermission[] = [];
        if (
          liveboardsAccessableByGroups &&
          liveboardsAccessableByGroups[0].metadata_permission_info.length !== 0
        ) {
          const arrayOfPermissions =
            liveboardsAccessableByGroups[0].metadata_permission_info.find(
              (arrayValue) => arrayValue.metadata_type === "LIVEBOARD",
            );

          if (arrayOfPermissions && arrayOfPermissions.metadata_permissions) {
            arrayOfPermissions.metadata_permissions.forEach((value) => {
              if (
                !systemLiveboards.some(
                  (arrayValue) => arrayValue.id === value.metadata_id,
                )
              ) {
                liveboardsAccessableByThisGroup.push(value);
                if (value.permission === "READ_ONLY") {
                  visibleLiveboardsReadOnly = visibleLiveboardsReadOnly.filter(
                    (liveboard) => liveboard.id !== value.metadata_id,
                  );
                } else {
                  visibleLiveboardsModify = visibleLiveboardsModify.filter(
                    (liveboard) => liveboard.id !== value.metadata_id,
                  );
                }
              }
            });
          }
        }
        groupsAndLiveboardAccess.push({
          userGroup: userGroups[i],
          liveboardArray: liveboardsAccessableByThisGroup,
        });
      }

      setState((prevState) => ({
        ...prevState,
        liveboardAccess: {
          group: groupsAndLiveboardAccess,
          directReadOnly: visibleLiveboardsReadOnly,
          directModify: visibleLiveboardsModify,
        },
        loadingSharingData: false,
        lifecycle: null,
      }));
    }
  }

  async function handleGetGroupInfo() {
    if (state.tsToken && props.mode === "Group") {
      setState((prevState) => ({
        ...prevState,
        lifecycle: "Fetching liveboards shared directly with user",
        loadingSharingData: true,
      }));

      const groupsAndLiveboardAccess: {
        userGroup: { id: string; name: string };
        liveboardArray: MetadataPermission[];
      }[] = [];

      setState((prevState) => ({
        ...prevState,
        lifecycle:
          "Fetching liveboards accessible by group " +
          state.selectedPrincipal.label,
      }));

      const liveboardsAccessableByGroups =
        await liveboardsAccessableByGroupOrUser({
          thoughtSpotAccessToken: state.tsToken,
          IdArray: [
            { identifier: state.selectedPrincipal.value, type: "USER_GROUP" },
          ],
        }).unwrap();

      const liveboardsAccessableByThisGroup: MetadataPermission[] = [];
      if (
        liveboardsAccessableByGroups &&
        liveboardsAccessableByGroups[0].metadata_permission_info.length !== 0
      ) {
        const arrayOfPermissions =
          liveboardsAccessableByGroups[0].metadata_permission_info.find(
            (arrayValue) => arrayValue.metadata_type === "LIVEBOARD",
          );

        if (arrayOfPermissions && arrayOfPermissions.metadata_permissions) {
          arrayOfPermissions.metadata_permissions.forEach((value) => {
            if (!value.metadata_name.includes("(Sample)")) {
              liveboardsAccessableByThisGroup.push(value);
            }
          });
        }
      }
      groupsAndLiveboardAccess.push({
        userGroup: {
          name: state.selectedPrincipal.label,
          id: state.selectedPrincipal.value, // this isn't the actual ID of the group
        },
        liveboardArray: liveboardsAccessableByThisGroup,
      });

      setState((prevState) => ({
        ...prevState,
        liveboardAccess: {
          group: groupsAndLiveboardAccess,
          directReadOnly: [],
          directModify: [],
        },
        loadingSharingData: false,
        lifecycle: null,
      }));
    }
  }

  useEffect(() => {
    if (state.selectedPrincipal.value) {
      if (state.tsToken) {
        if (props.mode === "User") {
          handleGetUserInfo();
        } else {
          handleGetGroupInfo();
        }
      }
    }
  }, [state.selectedPrincipal]);

  const handleChange = (newValue: SingleValue<ReactSelectOption>) => {
    if (newValue === null) {
      setState({
        ...state,
        liveboardAccess: null,
        selectedPrincipal: { label: "", value: "" },
      });
    } else {
      setState({
        ...state,
        liveboardAccess: null,
        selectedPrincipal: newValue!,
      });
    }
  };

  return (
    <div className="module-master">
      <div className="module-title">
        {props.mode === "User" ? "User" : "Group"} Permission Tool
      </div>
      {state.loadingPrincipals && (
        <div style={{ textAlign: "center", padding: "5px" }}>
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            spin={true}
            style={{ margin: "0 auto" }}
          />
        </div>
      )}
      {!state.loadingPrincipals && (
        <div className="container-fluid">
          <div>
            <div
              style={{
                display: "flex",
                paddingRight: "5px",
                justifyContent: "space-between",
              }}
            >
              <div>
                Select {props.mode === "User" ? "User" : "Group"}:
                <div style={{ width: "250px" }}>
                  <Select
                    onChange={(selectedPrincipal) =>
                      handleChange(selectedPrincipal)
                    }
                    options={state.principalList}
                    className="basic-multi-select w-100"
                    classNamePrefix="select"
                    value={state.selectedPrincipal}
                  />
                </div>
              </div>
              <div>
                <div className="option-in-card">
                  <div>
                    <button
                      className="btn"
                      disabled={
                        state.currentlyFetchingCSVData ||
                        state.lifecycle !== null
                      }
                      onClick={() => {
                        handleClickGetCSV(
                          props.mode === "User"
                            ? "user_permission_matrix.csv"
                            : "group_permission_matrix.csv",
                        );
                      }}
                    >
                      Export All Permissions to CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            {state.loadingSharingData && (
              <div style={{ textAlign: "center", padding: "5px" }}>
                <FontAwesomeIcon
                  icon={faSpinner}
                  size="2x"
                  spin={true}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}
            {state.loadingSharingData && state.lifecycle !== null && (
              <div
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  paddingTop: "10px",
                }}
              >
                {state.lifecycle}
              </div>
            )}
            {!state.loadingSharingData &&
              state.liveboardAccess &&
              state.liveboardAccess.group
                .map((group) => {
                  return group.liveboardArray;
                })
                .flat().length > 0 &&
              state.liveboardAccess.group.length > 0 && (
                <div>
                  <div
                    style={{
                      fontWeight: "bold",
                      paddingTop: "10px",
                      paddingBottom: "10px",
                    }}
                  >
                    {props.mode === "User" ? "Access provided by groups" : ""}
                  </div>
                  <div>
                    {state.liveboardAccess.group.map((groupInfo) => {
                      if (groupInfo.liveboardArray.length > 0) {
                        return (
                          <LiveboardCard
                            orgId={props.orgId}
                            groupName={groupInfo.userGroup.name}
                            liveboardInfo={groupInfo.liveboardArray}
                            user={state.selectedPrincipal}
                          />
                        );
                      } else {
                        return <></>;
                      }
                    })}
                  </div>
                </div>
              )}
            {!state.loadingSharingData &&
              state.liveboardAccess &&
              (state.liveboardAccess.directReadOnly.length > 0 ||
                state.liveboardAccess.directModify.length > 0) && (
                <div>
                  <div
                    style={{
                      fontWeight: "bold",
                      paddingTop: "10px",
                      paddingBottom: "10px",
                    }}
                  >
                    Direct Access
                  </div>
                  <div>
                    {state.liveboardAccess.directReadOnly.length > 0 && (
                      <LiveboardCard
                        orgId={props.orgId}
                        groupName={"Direct Access (Read Only)"}
                        liveboardInfo={state.liveboardAccess.directReadOnly.map(
                          (liveboard) => {
                            return {
                              metadata_id: liveboard.id,
                              metadata_name: liveboard.name,
                              permission: "READ_ONLY",
                            };
                          },
                        )}
                        overrideText="The following liveboards were shared directly with the user (Read Only)"
                        user={state.selectedPrincipal}
                      />
                    )}
                    {state.liveboardAccess.directModify.length > 0 && (
                      <LiveboardCard
                        orgId={props.orgId}
                        groupName={"Direct Access (Can Edit)"}
                        liveboardInfo={state.liveboardAccess.directModify.map(
                          (liveboard) => {
                            return {
                              metadata_id: liveboard.id,
                              metadata_name: liveboard.name,
                              permission: "MODIFY",
                            };
                          },
                        )}
                        user={state.selectedPrincipal}
                        overrideText="The following liveboards were shared directly with the user (Can Edit)"
                      />
                    )}
                  </div>
                </div>
              )}
            {!state.loadingSharingData &&
              state.liveboardAccess &&
              !(
                state.liveboardAccess.directReadOnly.length > 0 ||
                state.liveboardAccess.directModify.length > 0 ||
                state.liveboardAccess.group
                  .map((group) => {
                    return group.liveboardArray;
                  })
                  .flat().length > 0
              ) && (
                <div>
                  {props.mode === "User" ? "User" : "Group"} has no shared
                  liveboards.
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
